import {onUnmounted, ref, watch} from 'vue';

/**
 * Interface representing the options for the ReactiveLocalStorage class.
 */
interface StorageOptions {
    prefix?: string;
    serialize?: (value: any) => string;
    deserialize?: (value: string) => any;
}

/**
 * Class representing a reactive local storage.
 */
class ReactiveLocalStorage {
    private prefix: string;
    private subscribers: Map<string, Set<(value: any) => void>>;
    private serialize: (value: any) => string;
    private deserialize: (value: string) => any;

    /**
     * Creates an instance of ReactiveLocalStorage.
     * @param {StorageOptions} [options={}] - The options for the storage.
     */
    constructor(options: StorageOptions = {}) {
        this.prefix = options.prefix || '';
        this.subscribers = new Map();
        this.serialize = options.serialize || ((value: any) => {
            return typeof value !== 'string' ? JSON.stringify(value) : value;
        });
        this.deserialize = options.deserialize || ((value: string) => {
            try {
                return JSON.parse(value);
            } catch (e) {
                return value;
            }
        });

        window.addEventListener('storage', this.handleStorageEvent);
    }

    /**
     * Sets a value in the local storage.
     * @param {string} key - The key to set.
     * @param {T} value - The value to set.
     * @returns {T} The value that was set.
     */
    set<T>(key: string, value: T): T {
        const fullKey = this.getFullKey(key);
        localStorage.setItem(fullKey, this.serialize(value));
        this.notifySubscribers(key, value);
        return value;
    }

    /**
     * Gets a value from the local storage.
     * @param {string} key - The key to get.
     * @param {T | null} [defaultValue=null] - The default value if the key does not exist.
     * @returns {T | null} The value from the local storage or the default value.
     */
    get<T>(key: string, defaultValue: T | null = null): T | null {
        const fullKey = this.getFullKey(key);
        const value = localStorage.getItem(fullKey);

        if (value === null) {
            return defaultValue;
        }

        return this.deserialize(value);
    }

    /**
     * Removes a value from the local storage.
     * @param {string} key - The key to remove.
     */
    remove(key: string): void {
        const fullKey = this.getFullKey(key);
        localStorage.removeItem(fullKey);
        this.notifySubscribers(key, null);
    }

    /**
     * Clears all values from the local storage.
     */
    clear(): void {
        if (this.prefix) {
            const keysToRemove = Object.keys(localStorage)
                .filter(key => key.startsWith(this.prefix));

            keysToRemove.forEach(key => {
                localStorage.removeItem(key);
                const pureKey = key.slice(this.prefix.length);
                this.notifySubscribers(pureKey, null);
            });
        } else {
            localStorage.clear();

            for (const key of this.subscribers.keys()) {
                this.notifySubscribers(key, null);
            }
        }
    }

    /**
     * Subscribes to changes for a specific key.
     * @param {string} key - The key to subscribe to.
     * @param {(value: T | null) => void} callback - The callback to call when the value changes.
     * @returns {() => void} A function to unsubscribe from the changes.
     */
    subscribe<T>(key: string, callback: (value: T | null) => void): () => void {
        if (!this.subscribers.has(key)) {
            this.subscribers.set(key, new Set());
        }

        this.subscribers.get(key)?.add(callback);

        return () => {
            const keySubscribers = this.subscribers.get(key);
            if (keySubscribers) {
                keySubscribers.delete(callback);
                if (keySubscribers.size === 0) {
                    this.subscribers.delete(key);
                }
            }
        };
    }

    /**
     * Gets all keys from the local storage.
     * @returns {string[]} An array of keys.
     */
    keys(): string[] {
        return Object.keys(localStorage)
            .filter(key => !this.prefix || key.startsWith(this.prefix))
            .map(key => this.prefix ? key.slice(this.prefix.length) : key);
    }

    /**
     * Gets all values from the local storage.
     * @returns {T[]} An array of values.
     */
    values<T>(): T[] {
        return this.keys().map(key => this.get<T>(key)) as T[];
    }

    /**
     * Gets all entries from the local storage.
     * @returns {[string, T | null][]} An array of key-value pairs.
     */
    entries<T>(): [string, T | null][] {
        return this.keys().map(key => [key, this.get<T>(key)]);
    }

    /**
     * Destroys the instance and removes all event listeners.
     */
    destroy(): void {
        window.removeEventListener('storage', this.handleStorageEvent);
        this.subscribers.clear();
    }

    /**
     * Handles the storage event.
     * @param {StorageEvent} event - The storage event.
     */
    private handleStorageEvent = (event: StorageEvent): void => {
        if (event.storageArea === localStorage && event.key) {
            if (this.prefix && !event.key.startsWith(this.prefix)) {
                return;
            }

            const key = this.prefix ? event.key.slice(this.prefix.length) : event.key;
            const value = event.newValue !== null
                ? this.deserialize(event.newValue)
                : null;

            this.notifySubscribers(key, value);
        }
    };

    /**
     * Gets the full key with the prefix.
     * @param {string} key - The key to get the full key for.
     * @returns {string} The full key.
     */
    private getFullKey(key: string): string {
        return this.prefix + key;
    }

    /**
     * Notifies subscribers of a change.
     * @param {string} key - The key that changed.
     * @param {any} value - The new value.
     */
    private notifySubscribers(key: string, value: any): void {
        const keySubscribers = this.subscribers.get(key);
        if (keySubscribers) {
            keySubscribers.forEach(callback => {
                callback(value);
            });
        }
    }
}

const storage = new ReactiveLocalStorage({prefix: 'app:'});

/**
 * Custom hook to use reactive storage in Vue components.
 * @param {string} key - The key to use in the storage.
 * @param {T} [defaultValue] - The default value if the key does not exist.
 * @returns {Ref<T | null>} A ref to the stored value.
 */
export function useStorage<T>(key: string, defaultValue?: T) {
    const storedValue = ref<T | null>(storage.get<T>(key, defaultValue as T || null));

    const unsubscribe = storage.subscribe<T>(key, (newValue) => {
        storedValue.value = newValue;
    });

    watch(storedValue, (newValue) => {
        if (newValue === null) {
            storage.remove(key);
        }
    }, {deep: true});

    onUnmounted(() => {
        unsubscribe();
    });

    return storedValue;
}

export {storage};
export default storage;